import { useMemo, useState } from 'react';
import { Ban } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import DataTable from '../ui/DataTable';
import SeriesChart from './SeriesChart';
import DateRangeFilter from './DateRangeFilter';
import InlineNotice from './InlineNotice';
import { useTrafficReport } from '../../hooks/admin/useAdminSecurity';
import { chartColors } from '../../lib/chartColors';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/utils';
import type { TrafficRow, TrafficScope } from '../../types/admin';

const HOURS_OPTIONS = [
  { value: 24, label: '24h' },
  { value: 168, label: '7d' },
  { value: 720, label: '30d' },
] as const;

interface TrafficDrilldownModalProps {
  scope: TrafficScope | null;
  onClose: () => void;
  /** Human label for the title, e.g. the email or org name. */
  label?: string;
  /** When the scope is an IP, offers a "Block this IP" button. */
  onBlockIp?: (ip: string) => void;
}

type RowWithId = TrafficRow & { id: string };

function scopeLabel(scope: TrafficScope): string {
  if ('ip' in scope) return `IP ${scope.ip}`;
  if ('userId' in scope) return `user ${scope.userId}`;
  return `organisation ${scope.organizationId}`;
}

function shortTs(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-MY', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function TrafficDrilldownModal({ scope, onClose, label, onBlockIp }: TrafficDrilldownModalProps) {
  const [hours, setHours] = useState<number>(168);
  const query = useTrafficReport(scope, hours);
  const rows = useMemo<RowWithId[]>(
    () => (query.data?.rows ?? []).map((r) => ({ ...r, id: `${r.method} ${r.routePattern}` })),
    [query.data]
  );
  const ip = scope && 'ip' in scope ? scope.ip : null;

  return (
    <Modal
      isOpen={Boolean(scope)}
      onClose={onClose}
      title={scope ? `Traffic · ${label ?? scopeLabel(scope)}` : 'Traffic'}
      size="xl"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            {ip && onBlockIp && (
              <Button variant="destructive" size="sm" onClick={() => onBlockIp(ip)}>
                <Ban className="mr-2 h-4 w-4" aria-hidden />
                Block this IP
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-glass-text-2">
            Requests per route pattern for the selected window (aggregated 1-minute buckets).
          </p>
          <DateRangeFilter value={hours} options={HOURS_OPTIONS} onChange={setHours} />
        </div>

        {query.isError && <InlineNotice tone="error">{getApiErrorMessage(query.error, 'Failed to load traffic')}</InlineNotice>}

        <div className="glass-card p-4">
          <SeriesChart
            data={query.data?.series ?? []}
            xKey="ts"
            type="area"
            stacked={false}
            height={200}
            loading={query.isPending && Boolean(scope)}
            xFormatter={shortTs}
            series={[
              { key: 'total', name: 'Requests', color: chartColors.accent },
              { key: 's4xx', name: '4xx', color: chartColors.warn },
              { key: 's5xx', name: '5xx', color: chartColors.danger },
            ]}
          />
        </div>

        <div className="glass-card overflow-hidden">
          <DataTable<RowWithId>
            data={rows}
            isLoading={query.isPending && Boolean(scope)}
            emptyMessage="No requests recorded in this window."
            columns={[
              {
                header: 'Route',
                accessor: (r) => (
                  <span className="font-mono text-xs">
                    <span className="mr-2 text-glass-text-3">{r.method}</span>
                    {r.routePattern}
                  </span>
                ),
              },
              { header: 'Requests', accessor: (r) => r.count.toLocaleString(), className: 'text-glass-text tabular-nums' },
              {
                header: '4xx',
                accessor: (r) => (r.s4xx > 0 ? <span className="text-[rgb(var(--warn))]">{r.s4xx}</span> : '0'),
                className: 'tabular-nums',
              },
              {
                header: '5xx',
                accessor: (r) => (r.s5xx > 0 ? <span className="text-[rgb(var(--danger))]">{r.s5xx}</span> : '0'),
                className: 'tabular-nums',
              },
              { header: 'Avg ms', accessor: (r) => Math.round(r.avgDurationMs ?? 0).toLocaleString(), className: 'tabular-nums text-glass-text-2' },
              {
                header: 'Last seen',
                accessor: (r) => (r.lastSeen ? formatDateTime(r.lastSeen) : '—'),
                className: 'text-glass-text-2',
              },
            ]}
          />
        </div>
      </div>
    </Modal>
  );
}
